import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Header.css";
import logo from "../Header/images/header2.png";
import {
  FaHome,
  FaShieldAlt,
  FaFileContract,
  FaGlobe,
  FaChevronDown,
} from "react-icons/fa";
import { useLanguage } from "../Authentication/LanguageContext";

const languageOptions = [
  { value: "en", label: "English" },
  { value: "ta", label: "தமிழ்" },
  { value: "fr", label: "Français" },
  { value: "hi", label: "हिंदी" },
  { value: "es", label: "Español" },
  { value: "ar", label: "العربية" },
  { value: "ru", label: "Русский" },
  { value: "zh", label: "中文" },
  { value: "si", label: "සිංහල" },
  { value: "tr", label: "Türkçe" },
];

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isHoveringLogo, setIsHoveringLogo] = useState(false);
  const { language, setLanguage } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolled]);

  const toggleMenu = () => setIsMenuOpen((s) => !s);

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLanguage(e.target.value as any);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!isMenuOpen) return;
      const target = e.target as HTMLElement;
      if (!target.closest(".nav-menu") && !target.closest(".hamburger-icon")) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  return (
    <header className={`header-wrapper ${scrolled ? "scrolled" : ""}`}>
      <div className="header-container">
        <div className="logo-section">
          <Link
            to="/"
            className="logo-link"
            onMouseEnter={() => setIsHoveringLogo(true)}
            onMouseLeave={() => setIsHoveringLogo(false)}
            onClick={() => setIsMenuOpen(false)}
          >
            <img
              src={logo}
              alt="German A1-C2 Logo"
              className={`logo-image ${isHoveringLogo ? "hover" : ""}`}
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "https://via.placeholder.com/150x50?text=Logo";
              }}
            />
            <span className="logo-glow"></span>
          </Link>

          <div className="language-selector">
            <FaGlobe className="language-icon" />
            <select
              id="lang"
              value={language}
              onChange={handleLanguageChange}
              aria-label="Select language"
            >
              {languageOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <FaChevronDown className="language-chevron" />
          </div>
        </div>

        <nav className={`nav-menu ${isMenuOpen ? "active" : ""}`}>
          <Link
            to="/"
            className={`nav-link ${location.pathname === "/" ? "active" : ""}`}
            onClick={() => setIsMenuOpen(false)}
          >
            <FaHome className="icon" />
            <span className="nav-text">Home</span>
            <span className="nav-highlight"></span>
          </Link>

          <Link
            to="/privacy-policy"
            className={`nav-link ${
              location.pathname === "/privacy-policy" ? "active" : ""
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            <FaShieldAlt className="icon" />
            <span className="nav-text">Privacy Policy</span>
            <span className="nav-highlight"></span>
          </Link>

          <Link
            to="/terms-of-service"
            className={`nav-link ${
              location.pathname === "/terms-of-service" ? "active" : ""
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            <FaFileContract className="icon" />
            <span className="nav-text">Terms of Service</span>
            <span className="nav-highlight"></span>
          </Link>
        </nav>

        <button
          className={`hamburger-icon ${isMenuOpen ? "active" : ""}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
        >
          <div className="hamburger-box">
            <span className="hamburger-line top"></span>
            <span className="hamburger-line middle"></span>
            <span className="hamburger-line bottom"></span>
          </div>
          {!isMenuOpen && <span className="menu-text">MENU</span>}
        </button>
      </div>
    </header>
  );
};

export default Header;
